import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormGroupDirective, ValidatorFn, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatRadioChange} from '@angular/material/radio';
import {ActivatedRoute, Router} from '@angular/router';
import {KeycloakService} from 'keycloak-angular';
import {forkJoin, merge, Observable, Observer, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';

import {Common} from '../../../../../common/util/Util';
import {CaseType} from '../../../../types';
import {CaseHospitalDTO} from '../../../../models/case-hospital/CaseHospitalDTO';
import {CaseFuneralService} from '../../../../services/case-funeral.service';
import {ClaimService} from '../../../../services/claim.service';
import {ClmRequestBuilder} from '../../../../services/clm-request-builder';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {FileUploadService} from '../../../../../common/services/file-upload.service';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {InsuredPersonDTO} from '../../../../../ent/models/InsuredPersonDTO';
import {InsuredPersonWithDatesDTO} from '../../../../../ent/models/InsuredPersonWithDatesDTO';
import {InsuredPersonService} from '../../../../../ent/services/insured-person.service';
import {LookupValue} from '../../../../types/lookup-value.enum';
import {LookupService} from '../../../../../common/services/lookup.service';
import {LocationService} from '../../../../../ent/services/location.service';
import {CreateNameValueDTO, NameValueDTO} from '../../../../../common/models/NameValueDTO';
import {PolicyDTO} from '../../../../../ent/models/PolicyDTO';
import {ProductDTO} from '../../../../../ent/models/ProductDTO';
import {PolicyService} from '../../../../../ent/services/policy.service';
import {ProductService} from '../../../../../ent/services/product.service';
import {ProviderService} from '../../../../../ent/services/provider.service';
import {SharedService} from '../../../../../common/services/shared-service';
import {SharedClmService} from '../../../../services/shared-clm.service';
import {HttpApiService} from '../../../../../common/services/http-api-service';
import {CaseFuneralDTO} from '../../../../models/CaseFuneralDTO';
import {HelpInfo} from '../../../../types/help-info.enum';
import {SharedFormService} from '../../../../../common/services/shared-form-service';
import {DatePipe} from '@angular/common';
import {CaseDTO} from '../../../../models/CaseDTO';
import {CreateDataColumnDTO, DataColumnDTO} from '../../../../../common/models/DataColumnDTO';
import {CreateDataOverrideDTO, DataOverrideDTO} from '../../../../../common/models/DataOverrideDTO';
import {DataOverrideService} from '../../../../../common/services/data-override.service';
import {Role} from '../../../../types/role.enum';
import {environment} from "../../../../../../../environments/environment";

enum SearchType {
  MEMBER = 'Member Search',
  POLICY = 'Policy Search'
}

enum Controls {
  PROCESSING_CLASS = 'processingClass',
  INSURED_PERSON_NAME = 'insuredPersonName',
  ICD_CATEGORY = 'icdCategory',
  POLICY = 'policy',
  PRODUCT = 'product',
  MEMBER_OR_POLICY = 'memberOrPolicy',
  ID_NUMBER_OVERRIDE = 'idNumberOverride'
}

@Component({
  selector: 'app-case-association-step',
  templateUrl: './case-association-step.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_add-view-edit-styles.scss',
    './case-association-step.component.scss']
})
export class CaseAssociationStepComponent implements OnChanges, OnInit, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly dateFormat = DateUtil.dateFormat;
  readonly controls = Controls;
  readonly caseType = CaseType;
  readonly searchType = SearchType;
  readonly icdCategoryTextChanged$ = new Subject<string>();
  readonly insuredPersonNameTextChanged$ = new Subject<string>();
  readonly policyNumberTextChanged$ = new Subject<string>();

  @Input() type: CaseType;
  @Input() data: CaseFuneralDTO|CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;

  @Output() personId: EventEmitter<number> = new EventEmitter();
  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() updateHeading: EventEmitter<string> = new EventEmitter();

  // Note:
  // Used Admission Step as readonly value
  @Output() updateIcdCategory: EventEmitter<string> = new EventEmitter<string>();

  heading: string;

  products: ProductDTO[];
  selectedPolicy: PolicyDTO;
  selectedProduct: ProductDTO;
  selectedInsuredPerson: InsuredPersonDTO;
  selectedInsuredPersonWithDates: InsuredPersonWithDatesDTO;
  filteredInsuredPersons: InsuredPersonDTO[];
  filteredPolicies: PolicyDTO[];
  editedCase: CaseDTO;

  processingClassFormControl: FormControl;
  caseNameFormControl: FormControl;
  icdCategoryNameFormControl: FormControl;
  insuredPersonNameFormControl: FormControl;
  policyFormControl: FormControl;
  productFormControl: FormControl;
  memberOrPolicyFormControl: FormControl;
  idNumberOverrideFormControl: FormControl;
  icdCategories: NameValueDTO[] = [];

  private loggedInUserName: string;
  userRoles: string[];
  editIdNumber: boolean;

  constructor(
    private claimService: ClaimService,
    private caseFuneralService: CaseFuneralService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private fileUploadService: FileUploadService,
    private httpService: HttpApiService,
    private keycloakService: KeycloakService,
    private lookupService: LookupService,
    private locationService: LocationService,
    private formGroupDirective: FormGroupDirective,
    private policyService: PolicyService,
    private productService: ProductService,
    private providerService: ProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    private sharedClmService: SharedClmService,
    private sharedFormService: SharedFormService,
    private insuredPersonService: InsuredPersonService,
    public datePipe: DatePipe,
    private dataOverrideService: DataOverrideService) {
    this.filteredPolicies = [];
    this.products = [];
  }

  get hasProducts(): boolean {
    return !!this.products?.length;
  }

  get isPersonAndPolicySelected() {
    return this.isInsuredPersonSelected && this.isPolicySelected;
  }

  private get isInsuredPersonSelected(): boolean {
    return !!this.selectedInsuredPerson;
  }

  private get isPolicySelected(): boolean {
    return !!this.selectedPolicy;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.type) {
      this.type = changes.type.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.setFormsForEdit(this.data);
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initHelpInfo();
    this.initAssociationStepFormControls();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initHelpInfo() {
    if (this.type === this.caseType.Funeral) {
      this.getHelpInfo(HelpInfo.GeneralCase);
    }
    else if (this.type === this.caseType.Medical) {
      this.getHelpInfo(HelpInfo.GeneralCase);
      this.getHelpInfo(HelpInfo.HospitalCase);
    }
  }

  initSubscriptions() {

    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });

    this.userRoles = this.keycloakService.getUserRoles();

    // ----------------------------------------

    this.icdCategoryTextChanged$.pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
          this.getICDCategoriesListByText(value);
        },
        error => console.log(error));
    // ----------------------------------------
    this.insuredPersonNameTextChanged$.pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
          this.getListOfInsuredPersons(value);
        },
        error => console.log(error));

    this.policyNumberTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
          this.getListOfPolicies(value);
        },
        error => console.log(error));
  }

  initAssociationStepFormControls() {
    this.processingClassFormControl = new FormControl({
      value: this.type,
      disabled: true
    }, [Validators.required]);
    this.caseNameFormControl = new FormControl(null, [Validators.required]);
    this.icdCategoryNameFormControl = new FormControl(null);
    this.insuredPersonNameFormControl = new FormControl(null, [Validators.required]);
    this.policyFormControl = new FormControl({value: null, disabled: true}, [Validators.required]);
    this.productFormControl = new FormControl({value: null, disabled: true}, [Validators.required]);
    this.memberOrPolicyFormControl = new FormControl(this.searchType.MEMBER);
    this.idNumberOverrideFormControl = new FormControl(''); // required when editing only

    // Bind FormGroup with FormControls using FormBuilder
    this.formGroup = this.formBuilder.group({
      [this.controls.PROCESSING_CLASS]: this.caseNameFormControl, // TODO: THIS IS WRONG BINDING BUT WILL CONFIRM
      [this.controls.INSURED_PERSON_NAME]: this.insuredPersonNameFormControl,
      [this.controls.ICD_CATEGORY]: this.icdCategoryNameFormControl,
      [this.controls.POLICY]: this.policyFormControl,
      [this.controls.PRODUCT]: this.productFormControl,
      [this.controls.MEMBER_OR_POLICY]: this.memberOrPolicyFormControl,
      [this.controls.ID_NUMBER_OVERRIDE]: this.idNumberOverrideFormControl
    },
      {validator: this.overridesSavedValidator()}
    );

    if (this.type === CaseType.Funeral) {
      this.sharedFormService.setValidator(this.formGroup, this.controls.ICD_CATEGORY, [Validators.required]);
    }

    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);

    // Emit Updated FormGroup controls
    merge(
      this.formGroup.get(this.controls.PROCESSING_CLASS).valueChanges,
      this.formGroup.get(this.controls.INSURED_PERSON_NAME).valueChanges,
      this.formGroup.get(this.controls.ICD_CATEGORY).valueChanges,
      this.formGroup.get(this.controls.POLICY).valueChanges,
      this.formGroup.get(this.controls.PRODUCT).valueChanges,
      this.formGroup.get(this.controls.MEMBER_OR_POLICY).valueChanges
    )
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(_ => {
        // Note: Emit Always:
        // Case Add/Edit select or reset values in both case
        this.updateForm.next(this.formGroup);
      });
  }

  getProductDisplayName(product: ProductDTO) {
    return `${product.productCode}: ${product.name} (${product.productStartDateForPerson} -
        ${product.productEndDateForPerson !== null ? product.productEndDateForPerson : ''})`;
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onInsuredPersonSelected(name: string) {
    if (this.filteredInsuredPersons) {
      this.selectedInsuredPerson = this.filteredInsuredPersons
        .find((insuredPerson: InsuredPersonDTO) =>
        name === insuredPerson.displayValue
      );

      setTimeout(() => {
        if (this.selectedInsuredPerson?.insuredPersonId) {
          this.personId.next(this.selectedInsuredPerson?.insuredPersonId);
        }
      }, 100);
    }

    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;

    if (memberOrPolicyValue === this.searchType.MEMBER) {
      this.clearPolicy();
      this.clearProduct();
    }
    else if (memberOrPolicyValue === this.searchType.POLICY) {
      this.toggleProductField();
    }
    this.updateData();
  }

  onBlurInsuredPersonName() {
    if (
      this.filteredInsuredPersons &&
      this.filteredInsuredPersons.length > 0
    ) {
      const value = this.insuredPersonNameFormControl.value;
      const isMatched = this.filteredInsuredPersons.find((item: InsuredPersonDTO) => value === item.displayValue);
      if (!isMatched) {
        this.clearInsuredPersonName();
      }
    }
    else {
      this.clearInsuredPersonName();
      this.clearInsuredPersonWithDates();
    }
  }

  onBlurPolicyNumber() {
    if (
      this.filteredPolicies &&
      this.filteredPolicies.length > 0
    ) {
      const value = this.policyFormControl.value;
      const isMatched = this.filteredPolicies.find((item: PolicyDTO) => value === this.getPolicyDisplayString(item));
      if (!isMatched) {
        this.clearPolicy();
      }
    }
    else {
      this.clearPolicy();
    }
  }

  onBlurICDCategory() {
    if (this.icdCategories && this.icdCategories.length > 0) {
      const value = this.icdCategoryNameFormControl.value;
      const isMatched = this.icdCategories.find((item: NameValueDTO) => value === item.name);
      if (!isMatched) {
        this.icdCategoryNameFormControl.reset();
      }
    }
    else {
      this.icdCategoryNameFormControl.reset();
    }
  }

  onPolicySelected(name: string) {
    if (this.filteredPolicies) {
      this.selectedPolicy = this.filteredPolicies.find((item: PolicyDTO) => name === this.getPolicyDisplayString(item));
    }
    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;

    if (memberOrPolicyValue === this.searchType.MEMBER) {
      this.toggleProductField();
    }
    else if (memberOrPolicyValue === this.searchType.POLICY) {
      this.clearInsuredPersonName();
      this.clearProduct();
    }
    this.updateData();
  }

  onProductSelected(id: number) {
    if (this.products) {
      this.selectedProduct = this.products.find((item: ProductDTO) => id === item.productId);
    }
    this.updateData();
  }

  resetAssociationFormGroup() {
    this.resetDependentLinkedPoliciesAndProducts();
    this.formGroup.reset();
  }

  resetDependentLinkedPoliciesAndProducts() {
    this.filteredInsuredPersons = [];
    this.clearInsuredPersonName();
    this.clearPolicy();
    this.clearProduct();
    this.clearInsuredPersonWithDates();
  }

  resetLinkedPoliciesAndProducts() {
    this.clearPolicy();
    this.clearProduct();
  }

  clearInsuredPersonName() {
    this.insuredPersonNameFormControl.reset();
    this.selectedInsuredPerson = null;

    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;
    if (memberOrPolicyValue === this.searchType.POLICY) {
      this.toggleProductField();

      if (this.selectedPolicy == null) {
        this.insuredPersonNameFormControl.disable();
        this.filteredInsuredPersons = [];
      }

      if (this.isEditMode) {
        this.loadPersonsAndProducts$()
          .subscribe(
            () => {
              //  EMPTY subscribe to initiate the stream
            });
      }
    }
  }

  clearPolicy() {
    this.policyFormControl.reset();
    this.selectedPolicy = null;

    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;

    if (memberOrPolicyValue === this.searchType.MEMBER) {

      // Note: "Linked Policies" is associated with selected "Insured Person"
      if (this.selectedInsuredPerson == null) {
        this.policyFormControl.disable();
        this.filteredPolicies = [];
      }

      this.toggleProductField();

      if (this.isEditMode) {
        this.loadPolicesAndProducts$()
          .subscribe(
            () => {
              //  EMPTY subscribe to initiate the stream
            });
      }
    }
  }

  clearProduct() {
    this.productFormControl.reset();
    this.selectedProduct = null;

    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;

    if (memberOrPolicyValue === this.searchType.MEMBER) {
      if (this.selectedInsuredPerson == null) {
        this.productFormControl.disable();
        this.products = [];
      }

      if (this.isEditMode) {
        this.loadPolicesAndProducts$()
          .subscribe(
            () => {
              //  EMPTY subscribe to initiate the stream
            });
      }
    }
    else if (memberOrPolicyValue === this.searchType.POLICY) {
      this.toggleProductField();

      if (this.selectedPolicy == null) {
        this.productFormControl.disable();
        this.products = [];
      }

      if (this.isEditMode) {
        this.loadPersonsAndProducts$()
          .subscribe(
            () => {
              //  EMPTY subscribe to initiate the stream
            });
      }
    }
  }

  clearInsuredPersonWithDates() {
    this.selectedInsuredPersonWithDates = null;
  }

  createDateRangeString(startDate, endDate): string {
    return this.sharedClmService.createDateRangeString(startDate, endDate);
  }

  onUpdateCaseName($event) {
    this.updateCaseName($event.target.value);
  }

  onMemberOrPolicyRadioOptionValueChanged($event: MatRadioChange) {
    const value = $event.source.value;

    if (value === this.searchType.MEMBER) {
      this.insuredPersonNameFormControl.enable();
      this.policyFormControl.disable();
    }
    else if (value === this.searchType.POLICY) {
      this.policyFormControl.enable();
      this.insuredPersonNameFormControl.disable();
    }
    this.resetDependentLinkedPoliciesAndProducts();
  }

  getPolicyDisplayString(policy: PolicyDTO): string {
    return `${policy.name} - ${policy.number}`;
  }

  getAssociationDisplayString(selectedInsuredPerson: InsuredPersonDTO): string {
    return selectedInsuredPerson != null && selectedInsuredPerson.association != null ? selectedInsuredPerson.association.name : 'Principal';
  }

  getInsuredPersonNameDisplayString(insuredPerson: InsuredPersonDTO): string {
    const middleName = insuredPerson.middleName != null && insuredPerson.middleName.trim() != '' ? ` ${insuredPerson.middleName.trim()}` : '';
    return `${insuredPerson.firstName}${middleName} ${insuredPerson.lastName}`;
  }

  onICDCategorySelectionChange(category: string) {
    this.updateIcdCategory.next(category); // Displayed in CaseAdmission Step
  }

  getPayload() {
    return {
      insuredPerson: this.selectedInsuredPerson,
      policy: this.selectedPolicy,
      product: this.selectedProduct,
      caseDTO: {
        caseId: this.isEditMode ? this.data.caseDTO.caseId : null,
        name: this.caseNameFormControl.value + this.getCaseNameSuffix(),
        icdCategoryName: this.type === CaseType.Funeral ? this.icdCategoryNameFormControl.value : null,
        caseStatus: null,
        caseManager: null,
        insuredPerson: {value: this.selectedInsuredPerson.insuredPersonId, name: this.selectedInsuredPerson.lastName},
        processingClass: {
          value: this.getProcessingClass(this.type),
          name: this.processingClassFormControl.value
        },
        insuredPersonId: this.selectedInsuredPerson.insuredPersonId,
        startDate: new Date(), // TODO THIS CAN BE UPDATED BASED ON CASE-FUNERAL PAYLOAD USING CaseStatusHistory
        endDate: null,
        created: this.isEditMode ? this.data.caseDTO.created : new Date(),
        createdBy: this.isEditMode ? this.data.caseDTO.createdBy : this.loggedInUserName,
        updated: this.isEditMode ? new Date() : null,
        updatedBy: this.isEditMode ? this.loggedInUserName : null,
        tenantId: null
      }
    };
  }

  getCaseNameSuffix(): string {
    const date = this.editedCase != null ? this.editedCase.created : new Date();
    const timestamp = this.datePipe.transform(new Date(date), 'yyyyMMdd_HHmm');
    if (this.processingClassFormControl.value === this.caseType.Funeral) {
      return `_${timestamp}`;
    }
    else {
      const policyNumber = this.selectedPolicy ? this.selectedPolicy.number : '[policy-number]';
      const dependentNumber = this.selectedInsuredPerson && this.selectedInsuredPerson.cardNumber ? this.selectedInsuredPerson.cardNumber.toString().slice(-2) : '00';
      return `_${policyNumber}_${dependentNumber}_${timestamp}`;
    }
  }

  overrideIdNumberClicked($event) {
    this.idNumberOverrideFormControl.setValue(this.selectedInsuredPerson.nationalIdNumber);
    this.idNumberOverrideFormControl.markAsTouched();
    this.idNumberOverrideFormControl.setValidators([Validators.required]);
    this.idNumberOverrideFormControl.updateValueAndValidity();
    this.editIdNumber = $event;
  }

  showIdNumberContentField() {
    this.editIdNumber = false;
    this.idNumberOverrideFormControl.clearAsyncValidators();
    this.idNumberOverrideFormControl.setValidators(null);
    this.idNumberOverrideFormControl.updateValueAndValidity();
  }

  saveIdNumberOverride() {
    const idNumberNew = this.idNumberOverrideFormControl.value;
    if (this.selectedInsuredPerson.nationalIdNumber != idNumberNew) {
      this.selectedInsuredPerson.nationalIdNumber = idNumberNew;
      const overrideValue = idNumberNew;

      const dataEntityType = CreateNameValueDTO(1, 'insured_person');
      const dataColumnType = CreateNameValueDTO(1, 'varchar');
      const dataColumnDTO = CreateDataColumnDTO(8, 'national_id_number', dataColumnType);
      const dataOverride = this.createDataOverrideDTO(overrideValue, dataEntityType, dataColumnDTO);

      this.saveDataOverride(dataOverride);
    }
    this.showIdNumberContentField();
  }

  allowOverride(): boolean {
    return this.userRoles.includes(Role.ClaimsAssessor);
  }

  overridesSavedValidator(): ValidatorFn {
    return (group: FormGroup): { [key: string]: any } | null => {
      return !this.editIdNumber ? null : {overridesSaved: true};
    };
  }

  private createDataOverrideDTO(overrideValue: string, dataEntityType: NameValueDTO, dataColumnDTO: DataColumnDTO): DataOverrideDTO {
    const dataOverride = CreateDataOverrideDTO(this.selectedInsuredPerson.insuredPersonId, overrideValue, dataEntityType, dataColumnDTO);
    dataOverride.created = new Date();
    dataOverride.createdBy = this.loggedInUserName;
    return dataOverride;
  }

  private saveDataOverride(dataOverride: DataOverrideDTO) {
    this.dataOverrideService.saveOverride(dataOverride)
      .pipe(
        take(1)
      )
      .subscribe(
        () => {},
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private updateCaseName(name: string) {
    if (this.isEditMode) {
      this.heading = this.getEditHeading(name);
    } else {
      this.heading = this.getAddHeading(name);
    }
    this.updateHeading.next(this.heading);
  }

  private getEditHeading(name: string) {
    return `Edit Case - ${name}${this.getCaseNameSuffix()}`;
  }

  private getAddHeading(name: string) {
    return `Add Case - ${name}${this.getCaseNameSuffix()}`;
  }

  private getCoreCaseName(name: string) {
    // set index to the first occurrence of either underscore or dash
    const indexUnderscore = name.indexOf('_');
    const indexDash = name.indexOf('-');
    let index = indexDash < indexUnderscore ? indexDash : indexUnderscore;
    if (indexDash == -1 && indexUnderscore != -1) {
      index = indexUnderscore;
    } else if (indexUnderscore == -1 && indexDash != -1) {
      index = indexDash;
    }
    return index === -1 ? name : name.substring(0, index);
  }

  private getProcessingClass(type: string): number {
    if (type === CaseType.Medical) {
      return 1;
    }
    else if (type === CaseType.Funeral) {
      return 3;
    }
  }

  private setFormsForEdit(data: CaseFuneralDTO|CaseHospitalDTO) {
    const {caseDTO} = data;
    this.heading = this.getEditHeading(caseDTO.name);
    this.processingClassFormControl.setValue(caseDTO.processingClass.name);
    this.caseNameFormControl.setValue(this.getCoreCaseName(caseDTO.name));

    if (this.type === this.caseType.Funeral) {
      this.icdCategoryNameFormControl.setValue(caseDTO.icdCategoryName);
    }
    else if (this.type === this.caseType.Medical) {
      this.icdCategoryNameFormControl.setValue(null);
    }

    // Emit value to notify in Admission Step:
    this.onICDCategorySelectionChange(this.icdCategoryNameFormControl.value);
    const displayLastName = this.getInsuredPersonDisplayValue(caseDTO.insuredPerson);
    this.insuredPersonNameFormControl.setValue(displayLastName);
    this.policyFormControl.setValue(this.getPolicyDisplayString(caseDTO.policy));
    this.productFormControl.setValue(caseDTO.product.productId);
    this.selectedInsuredPerson = caseDTO.insuredPerson;
    this.selectedPolicy = caseDTO.policy;
    this.selectedProduct = caseDTO.product;
    this.memberOrPolicyFormControl.setValue(this.searchType.MEMBER);
    this.editedCase = caseDTO;
    this.personId.next(this.selectedInsuredPerson.insuredPersonId);
    this.updateData();
  }

  private toggleProductField() {
    if (this.isPersonAndPolicySelected) {
      this.productFormControl.enable();
    }
    else {
      this.productFormControl.disable();
    }
  }

  private getInsuredPersonDisplayValue(insuredPerson: InsuredPersonDTO): string {
    return `${insuredPerson.firstName} ${insuredPerson.lastName} - ${insuredPerson.cardNumber} - ${insuredPerson.nationalIdNumber}`;
  }

  private getListOfInsuredPersons(lastName: string): void {
    let pagedRequestDTO: PagedRequestDTO;
    if (new RegExp('^[0-9]+$').test(lastName) || lastName.includes('_')) {
      pagedRequestDTO = ClmRequestBuilder.getFilteredListOfInsuredPersonsByIdNumberPagedRequestDTO(lastName);
    }
    else {
      pagedRequestDTO = ClmRequestBuilder.getFilteredListOfInsuredPersonsPagedRequestDTO(lastName);
    }

    this.insuredPersonService.getListOfInsuredPersons(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<InsuredPersonDTO>) => {
          response.resultList = response.resultList.map((person: InsuredPersonDTO) => {
            person.displayValue = this.getInsuredPersonDisplayValue(person);
            return person;
          });
          this.filteredInsuredPersons = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getListOfPolicies(searchValue: string): void {
    let pagedRequestDTO: PagedRequestDTO;
    pagedRequestDTO = ClmRequestBuilder.getFilteredListOfPoliciesPagedRequestDTO(searchValue);

    this.policyService.getListOfPolicies(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<PolicyDTO>) => {
          response.resultList = response.resultList.map((policy: PolicyDTO) => {
            return policy;
          });
          this.filteredPolicies = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getICDCategoriesListByText(searchValue: string): void {
    const fragment = `diagnosis/filter-categories?category=${searchValue}&type=${environment.schema.diagnosisType}`;
    this.httpService.get<NameValueDTO[]>(fragment)
      .pipe(
        take(1),
      )
      .subscribe((icdCategories: NameValueDTO[]) => {
        this.icdCategories = icdCategories;
      });
  }

  private updateData() {
    this.updateDropdownList();
    this.updateInsuredPersonWithDates();
    this.updateCaseName(this.caseNameFormControl.value);
  }

  private updateDropdownList() {
    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;
    if (memberOrPolicyValue === this.searchType.MEMBER) {
      this.getPoliciesAndProducts();
    }
    else if (memberOrPolicyValue === this.searchType.POLICY) {
      this.getPersonsAndProducts();
    }
  }

  private getPoliciesAndProducts() {
    this.loadPolicesAndProducts$()
      .subscribe((IsLoaded: boolean) => {
        if (IsLoaded) {
          this.policyFormControl.enable();

          // Note: Products are associated with selected policy so if policy is not selected reload existing policies and associated
          // products.
          const isPolicyMatched = this.filteredPolicies.find((item: PolicyDTO) => this.getPolicyDisplayString(item) === this.policyFormControl.value);
          if (this.selectedPolicy && !isPolicyMatched) {
            this.clearPolicy();
            this.clearProduct();
            this.sharedService.openSnackBar('The policy\'s data has changed since the case was created - please update the policy and product fields accordingly', 'Dismiss');
          }
          else if (this.isEditMode) {
            this.productFormControl.enable();
          }
          this.showNotifications();
        }
      }, (errorResponse) => {
        const error = errorResponse.error;
        return error.businessMessage;
      });
  }

  private getPersonsAndProducts() {
    this.loadPersonsAndProducts$()
      .subscribe((IsLoaded: boolean) => {
        if (IsLoaded) {
          this.insuredPersonNameFormControl.enable();
          const isPersonMatched = this.filteredInsuredPersons.find((item: InsuredPersonDTO) => item.displayValue === this.insuredPersonNameFormControl.value);
          if (!isPersonMatched) {
            this.clearInsuredPersonName();
            this.clearProduct();
          }
          else if (this.isEditMode) {
            this.productFormControl.enable();
          }
          this.showNotifications();
        }
      }, (errorResponse) => {
        const error = errorResponse.error;
        return error.businessMessage;
      });
  }

  private updateInsuredPersonWithDates() {
    if (this.selectedInsuredPerson && this.selectedPolicy && this.selectedProduct) {
      this.insuredPersonService.getInsuredPersonWithDates(this.selectedInsuredPerson.insuredPersonId, this.selectedPolicy.policyId, this.selectedProduct.productId)
        .pipe(
          take(1)
        )
        .subscribe(
          (response: InsuredPersonWithDatesDTO) => {
            this.selectedInsuredPersonWithDates = response;
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
    }
    else {
      this.clearInsuredPersonWithDates();
    }
  }

  private loadPersonsAndProducts$() {
    return new Observable((observer: Observer<any>) => {
      if (!this.selectedPolicy) {
        observer.next(false);
        return;
      }

      if (this.selectedInsuredPerson != null) {
        return forkJoin(
          this.insuredPersonService.getPolicyLinkedPersons(this.selectedPolicy.policyId, this.selectedProduct ? this.selectedProduct.productId : null, this.getProcessingClass(this.type)),
          this.productService.getInsuredPersonLinkedProducts(this.selectedInsuredPerson.insuredPersonId, this.selectedPolicy ? this.selectedPolicy.policyId : null, this.getProcessingClass(this.type))
        )
          .pipe(
            take(1),
          )
          .subscribe((responses: [InsuredPersonDTO[], ProductDTO[]]) => {
              if (responses[0] && responses[0].length > 0) {
                responses[0].map((person: InsuredPersonDTO) => {
                  person.displayValue = this.getInsuredPersonDisplayValue(person);
                  return person;
                });
                this.filteredInsuredPersons = [...responses[0]];
              }
              if (responses[1] && responses[1].length > 0) {
                const productsByProcessingClass = this.getProductsOfChosenProcessingClass(responses[1]);
                this.products = [...productsByProcessingClass];
              }
              observer.next(true);
            },
            (errorResponse) => {
              observer.next(false);
              const error = errorResponse.error;
              return error.businessMessage;
            });
      }
      else {
        return this.insuredPersonService.getPolicyLinkedPersons(this.selectedPolicy.policyId, this.selectedProduct ? this.selectedProduct.productId : null, this.getProcessingClass(this.type))
          .pipe(
            take(1),
          )
          .subscribe((response: InsuredPersonDTO[]) => {
              if (response && response.length > 0) {
                response.map((person: InsuredPersonDTO) => {
                  person.displayValue = this.getInsuredPersonDisplayValue(person);
                  return person;
                });
                this.filteredInsuredPersons = response;
              }
              observer.next(true);
            },
            (errorResponse) => {
              observer.next(false);
              const error = errorResponse.error;
              return error.businessMessage;
            });
      }
    });
  }

  private loadPolicesAndProducts$() {
    return new Observable((observer: Observer<any>) => {
      if (!this.selectedInsuredPerson) {
        observer.next(false);
        return;
      }

      return forkJoin(
        this.policyService.getInsuredPersonLinkedPolicies(this.selectedInsuredPerson.insuredPersonId, this.selectedProduct ? this.selectedProduct.productId : null, this.getProcessingClass(this.type)),
        this.productService.getInsuredPersonLinkedProducts(this.selectedInsuredPerson.insuredPersonId, this.selectedPolicy ? this.selectedPolicy.policyId : null, this.getProcessingClass(this.type))
      )
        .pipe(
          take(1),
        )
        .subscribe((responses: [PolicyDTO[], ProductDTO[]]) => {
            if (responses[0] && responses[0].length > 0) {
              this.filteredPolicies = [...responses[0]];
            }
            if (responses[1] && responses[1].length > 0) {
              const productsByProcessingClass = this.getProductsOfChosenProcessingClass(responses[1]);
              this.products = [...productsByProcessingClass];
            }
            observer.next(true);
          },
          (errorResponse) => {
            observer.next(false);
            const error = errorResponse.error;
            return error.businessMessage;
          });
    });
  }

  private showNotifications() {
    const memberOrPolicyValue = this.formGroup.get(this.controls.MEMBER_OR_POLICY).value;
    if (memberOrPolicyValue === this.searchType.MEMBER && this.filteredPolicies.length === 0) {
      this.sharedService.openSnackBar(`No policies with a ${this.type} product are assigned to the selected member`, 'Dismiss');
    }
    if (memberOrPolicyValue === this.searchType.POLICY && this.filteredInsuredPersons.length === 0) {
      this.sharedService.openSnackBar(`No members with a ${this.type} product are assigned to the selected policy`, 'Dismiss');
    }
  }

  private getProductsOfChosenProcessingClass(products: ProductDTO[]) {
    if (products?.length) {
      const caseType = this.processingClassFormControl.value;
      return products.filter((product: ProductDTO) => product.processingClass.name === caseType);
    }
    return products;
  }
}
